import React, { useCallback, useEffect, useState } from 'react';
import {
  LifeBuoy,
  Search,
  RotateCcw,
  UserPlus,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  RefreshCw,
  Copy,
  Check,
  ShieldAlert,
  ArrowRightLeft,
} from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { invokeEdge } from '@/lib/edgeInvoke';
import { useI18n } from '@/i18n/LanguageContext';
import { money } from '@/data/constants';
import { WEBHOOK_PLAN_CENTS, WEBHOOK_PLAN_CREDITS } from '@/data/webhookSetup';

/**
 * Admin > Webhook & recovery (lower half).
 *
 * Recovers a payment the processor already collected but the CRM never told
 * the platform about. It NEVER touches a card: it replays the event through
 * the same signed webhook, which is idempotent on the provider transaction id,
 * so a second attempt cannot create a second credit, a second subscription or
 * a second charge record.
 *
 * Order of operations enforced by the UI:
 *   1. Look up the email            -> does a GigGone account exist?
 *   2. Create / convert the account -> reuses admin-create-subcontractor
 *   3. Replay the transaction       -> payment-recovery > payment-webhook
 *   4. Read back the evidence       -> event + payment + subscription + credits
 *
 * Every edge call goes through invokeEdge, so an expired admin session reports
 * itself as an expired session and can never be mistaken for "this customer
 * has no account" — the two used to render identically.
 */

interface PendingEvent {
  id: string;
  event_id: string;
  event_type: string;
  plan_code: string | null;
  email: string | null;
  amount: number;
  result: string | null;
  created_at: string;
}

const genPassword = () => {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789';
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  return `Gg-${Array.from(bytes).map((b) => chars[b % chars.length]).join('')}`;
};

const AdminPaymentRecovery: React.FC = () => {
  const { lang } = useI18n();
  const es = lang === 'es';

  const [email, setEmail] = useState('');
  const [transactionId, setTransactionId] = useState('');
  const [plan, setPlan] = useState('basic');
  const [contactId, setContactId] = useState('');
  const [subscriptionId, setSubscriptionId] = useState('');
  const [reason, setReason] = useState('CRM webhook was not delivered at the time of payment');

  const [lookup, setLookup] = useState<any>(null);
  /**
   * Explicit state machine. The four possible outcomes used to collapse into
   * one another — any failure rendered as "No GIGGONE account uses that email",
   * which is a claim about the CUSTOMER that a failed ADMIN call can never
   * support. They are now distinct and mutually exclusive:
   *   'idle'        nothing looked up yet
   *   'auth'        the admin session was rejected  (says nothing about the customer)
   *   'error'       the lookup itself failed        (says nothing about the customer)
   *   'none'        the function positively confirmed no profile uses that email
   *   'homeowner'   a profile exists but cannot hold membership credits yet
   *   'contractor'  a profile exists and is credit-eligible
   */
  const [lookupState, setLookupState] =
    useState<'idle' | 'auth' | 'error' | 'none' | 'homeowner' | 'contractor'>('idle');
  const [lookupError, setLookupError] = useState('');
  const [authError, setAuthError] = useState('');
  const [actionError, setActionError] = useState('');
  const [looking, setLooking] = useState(false);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState<any>(null);
  const [creating, setCreating] = useState(false);
  const [tempPassword, setTempPassword] = useState('');
  const [copied, setCopied] = useState(false);
  const [pending, setPending] = useState<PendingEvent[]>([]);

  const loadPending = useCallback(async () => {
    const { data } = await supabase
      .from('mkt_payment_events')
      .select('id,event_id,event_type,plan_code,email,amount,result,created_at')
      .order('created_at', { ascending: false })
      .limit(25);
    setPending(
      ((data as PendingEvent[]) || []).filter((e) => e.result && e.result.startsWith('pending'))
    );
  }, []);

  useEffect(() => {
    loadPending();
  }, [loadPending]);

  const runLookup = async () => {
    if (!email.trim()) return;
    setLooking(true);
    setResult(null);
    setActionError('');
    const r = await invokeEdge('payment-recovery', {
      action: 'verify',
      email: email.trim().toLowerCase(),
    });
    setLooking(false);

    // 1. Admin session rejected — this is about the operator, not the customer.
    if (r.authFailed) {
      setAuthError(r.message || 'unauthorized');
      setLookupError('');
      setLookup(null);
      setLookupState('auth');
      return;
    }
    setAuthError('');

    // 2. A trustworthy answer is one that actually carries accountExists.
    const payload: any = r.data;
    if (payload && typeof payload.accountExists === 'boolean') {
      setLookup(payload);
      setLookupError('');
      if (!payload.accountExists) {
        setLookupState('none');
      } else {
        setLookupState(payload.creditEligible === false ? 'homeowner' : 'contractor');
      }
      return;
    }

    // 3. Anything else (500, network, unexpected shape) is a FAILED LOOKUP.
    //    It must never be reported as "the customer has no account".
    setLookup(null);
    setLookupState('error');
    setLookupError(r.message || r.error || 'lookup_failed');
  };


  /** Create a brand-new contractor account, or convert an existing profile. */
  const createAccount = async (convertExisting = false) => {
    setCreating(true);
    setActionError('');
    setAuthError('');
    const password = genPassword();

    const r = await invokeEdge('admin-create-subcontractor', {
      email: email.trim().toLowerCase(),
      password,
      contactName: 'Recovered customer',
      companyName: 'Recovered customer',
      language: lang,
      convertExisting,
    });
    setCreating(false);

    if (r.authFailed) {
      setAuthError(r.message || 'unauthorized');
      return;
    }
    if (r.ok && r.data?.ok) {
      // A converted profile keeps the customer's own password, so only show a
      // temporary one when an auth user was actually created.
      if (r.data.mode === 'created' || r.data.mode === 'profile_repaired') setTempPassword(password);
      await runLookup();
      return;
    }
    if (r.error === 'already_exists' && r.data?.convertible && !convertExisting) {
      setActionError(
        es
          ? `Ese correo ya pertenece a una cuenta de tipo "${r.data.userType}". Use "Convertir a contratista" para poder asignarle los créditos.`
          : `That email already belongs to a "${r.data.userType}" account. Use “Convert to contractor” so the credits can be assigned.`
      );
      await runLookup();
      return;
    }
    setActionError(r.message || r.error || 'create_failed');
  };

  const runRecovery = async () => {
    setRunning(true);
    setResult(null);
    setActionError('');
    const r = await invokeEdge('payment-recovery', {
      action: 'recover',
      email: email.trim().toLowerCase(),
      transactionId: transactionId.trim(),
      plan,
      amount: WEBHOOK_PLAN_CENTS[plan],
      contactId: contactId.trim() || undefined,
      subscriptionId: subscriptionId.trim() || undefined,
      reason,
    });
    setRunning(false);

    if (r.authFailed) {
      setAuthError(r.message || 'unauthorized');
      return;
    }
    setResult(r.data || { ok: false, error: r.error });
    await Promise.all([runLookup(), loadPending()]);
  };

  const copyPassword = async () => {
    try {
      await navigator.clipboard.writeText(tempPassword);
    } catch {
      /* selectable on screen */
    }
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1800);
  };

  const accountExists = lookupState === 'contractor' || lookupState === 'homeowner';
  const wrongUserType = lookupState === 'homeowner';
  const canRecover = lookupState === 'contractor' && transactionId.trim().length >= 6 && !running;
  const ev = result?.evidence;

  // ---- derived, human-readable status lines for an existing account -------
  const sub = lookup?.subscriptions?.[0] || null;
  const lastPayment = lookup?.payments?.[0] || null;
  const lastEvent = lookup?.events?.[0] || null;
  const planLabel = (code?: string | null) =>
    code ? code.charAt(0).toUpperCase() + code.slice(1) : '—';

  const membershipLine = sub
    ? `${planLabel(sub.plan)} · ${String(sub.status || '').toUpperCase()}${
        sub.periodEnd ? ` · ${es ? 'renueva' : 'renews'} ${new Date(sub.periodEnd).toLocaleDateString()}` : ''
      }`
    : es
    ? 'Sin membresía activa'
    : 'No membership';

  const creditsLine = sub
    ? `${Math.max((sub.includedLeads || 0) - (sub.leadsUsed || 0), 0)} ${
        es ? 'disponibles' : 'available'
      } (${sub.leadsUsed || 0}/${sub.includedLeads || 0} ${es ? 'usados' : 'used'})${
        lookup?.extraCredits ? ` · +${lookup.extraCredits} ${es ? 'extra' : 'extra'}` : ''
      }`
    : `${lookup?.credits ?? 0}`;

  const paymentLine = lastPayment
    ? `${money(lastPayment.amount || 0)} · ${lastPayment.status}${
        lastPayment.ref ? ` · ${lastPayment.ref}` : ''
      }`
    : es
    ? 'Ningún pago registrado'
    : 'No payment recorded';

  const subscriptionLine = sub
    ? `${sub.status} · ${es ? 'pago' : 'payment'} ${sub.paymentsMade || 0}/${sub.termMonths || 1} · ${
        sub.includedLeads || 0
      } ${es ? 'créditos incluidos' : 'included credits'}`
    : es
    ? 'Ninguna suscripción'
    : 'No subscription';

  const recoveryLine = (() => {
    const r = String(lastEvent?.result || '');
    if (r.includes('manual_admin_grant'))
      return es
        ? 'Otorgada a mano por un administrador · falta el ID real del CRM (reconciliación pendiente)'
        : 'Granted by hand by an admin · awaiting the real CRM transaction id (reconciliation pending)';
    if (r.includes('reconciled'))
      return es ? 'Reconciliada con la transacción real del CRM' : 'Reconciled with the real CRM transaction';
    if (/activated|renewed|plan_changed/.test(r))
      return es ? `Procesada por el webhook (${r})` : `Processed by the webhook (${r})`;
    if (r.startsWith('pending'))
      return es ? `Pago recibido sin asignar (${r})` : `Payment received but unassigned (${r})`;
    if (!lastEvent)
      return es ? 'No se necesitó ninguna recuperación' : 'No recovery was needed';
    return r || '—';
  })();

  const field =
    'w-full rounded-xl border border-slate-300 px-3.5 py-3 text-sm font-semibold text-slate-800 outline-none focus:border-orange-400';
  const labelCls = 'text-[11px] font-bold uppercase tracking-wide text-slate-500';


  return (
    <div className="mt-14 border-t border-slate-200 pt-10">
      <h2 className="flex items-center gap-2 text-xl font-extrabold text-slate-900">
        <LifeBuoy className="h-5 w-5 text-orange-500" />
        {es ? 'Recuperar un pago ya cobrado' : 'Recover an already-collected payment'}
      </h2>
      <p className="mt-1 max-w-3xl text-sm text-slate-500">
        {es
          ? 'Reenvía el evento que el CRM no entregó. No se llama a ningún procesador de pagos, por lo que el cliente no puede ser cobrado otra vez. Si el mismo ID de transacción ya fue procesado, el reenvío no hace nada.'
          : 'Re-delivers the event the CRM failed to send. No payment processor is called, so the customer cannot be charged again. If the same transaction id was already processed, the replay is a no-op.'}
      </p>

      {/* ---------- step 1: find the account ---------- */}
      <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-5">
        <p className="text-sm font-extrabold text-slate-900">
          {es ? 'Paso 1 · Busque la cuenta del cliente' : 'Step 1 · Find the customer account'}
        </p>
        <div className="mt-3 flex flex-wrap gap-2">
          <input
            type="email"
            inputMode="email"
            autoCapitalize="none"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={es ? 'correo del cliente que pagó' : 'email the customer paid with'}
            className={`${field} min-w-[240px] flex-1`}
          />
          <button
            onClick={runLookup}
            disabled={looking || !email.trim()}
            className="inline-flex min-h-[48px] items-center gap-2 rounded-xl bg-slate-900 px-5 text-sm font-bold text-white hover:bg-slate-800 disabled:opacity-50"
            style={{ touchAction: 'manipulation' }}
          >
            {looking ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
            {es ? 'Buscar' : 'Look up'}
          </button>
        </div>

        {/* --- outcome A: the ADMIN session was rejected --------------------- */}
        {authError && (
          <div className="mt-4 rounded-xl border border-red-200 bg-red-50/80 p-4">
            <p className="flex items-center gap-2 text-sm font-extrabold text-red-800">
              <ShieldAlert className="h-4 w-4" />
              {es ? 'Error de autenticación de administrador' : 'Admin authentication error'}
            </p>
            <p className="mt-1 text-[13px] text-red-800">{authError}</p>
            <p className="mt-1 text-[12px] font-semibold text-red-700">
              {es
                ? 'Esto NO dice nada sobre el cliente. No se cambió nada. Vuelva a iniciar sesión como administrador y repita la acción.'
                : 'This says nothing about the customer. Nothing was changed. Sign in again as an administrator and repeat the action.'}
            </p>
          </div>
        )}


        {/* --- outcome B: the lookup itself failed --------------------------- */}
        {lookupState === 'error' && (
          <div className="mt-4 rounded-xl border border-red-200 bg-red-50/80 p-4">
            <p className="flex items-center gap-2 text-sm font-extrabold text-red-800">
              <XCircle className="h-4 w-4" />
              {es ? 'La búsqueda falló' : 'The lookup failed'}
            </p>
            <p className="mt-1 text-[13px] text-red-800">{lookupError}</p>
            <p className="mt-1 text-[12px] font-semibold text-red-700">
              {es
                ? 'No se pudo comprobar la cuenta, así que no se sabe si existe. No se cambió nada. Inténtelo de nuevo.'
                : 'The account could not be checked, so whether it exists is unknown. Nothing was changed. Try again.'}
            </p>
          </div>
        )}

        {actionError && (
          <p className="mt-3 flex items-start gap-2 rounded-xl bg-amber-50 px-3 py-2 text-[13px] font-semibold text-amber-900">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            {actionError}
          </p>
        )}

        {/* --- outcome C: a profile exists (contractor or homeowner) --------- */}
        {accountExists && lookup && (
          <div className="mt-4">
            <div
              className={`rounded-xl border p-4 ${
                wrongUserType ? 'border-amber-200 bg-amber-50/70' : 'border-emerald-200 bg-emerald-50/70'
              }`}
            >
              <p
                className={`flex items-center gap-2 text-sm font-extrabold ${
                  wrongUserType ? 'text-amber-900' : 'text-emerald-900'
                }`}
              >
                {wrongUserType ? <ArrowRightLeft className="h-4 w-4" /> : <CheckCircle2 className="h-4 w-4" />}
                {es ? 'Cuenta encontrada' : 'Account found'} — {lookup.account?.email}
              </p>

              <dl className="mt-3 grid gap-x-6 gap-y-1 text-[13px] sm:grid-cols-2">
                {[
                  [
                    es ? 'Tipo de usuario' : 'User type',
                    wrongUserType
                      ? `${lookup.account?.userType} — ${es ? 'no puede recibir créditos' : 'cannot hold credits'}`
                      : `${lookup.account?.userType} (${es ? 'contratista' : 'contractor'})`,
                  ],
                  [es ? 'Estado de la cuenta' : 'Account status', lookup.account?.status || '—'],
                  [es ? 'Membresía' : 'Membership', membershipLine],
                  [es ? 'Créditos' : 'Credits', creditsLine],
                  [es ? 'Estado de recuperación' : 'Recovery status', recoveryLine],
                  [es ? 'Estado del pago' : 'Payment status', paymentLine],
                  [es ? 'Suscripción' : 'Subscription', subscriptionLine],
                  [
                    es ? 'Eventos de pago' : 'Payment events',
                    `${lookup.events?.length || 0}${
                      lookup.ledger?.length ? ` · ${lookup.ledger.length} ${es ? 'asientos' : 'ledger rows'}` : ''
                    }`,
                  ],
                ].map(([k, v]) => (
                  <div key={String(k)} className="flex justify-between gap-3 border-b border-white/70 py-1">
                    <dt className={`font-bold ${wrongUserType ? 'text-amber-800' : 'text-emerald-800'}`}>{k}</dt>
                    <dd className="text-right font-semibold text-slate-900">{String(v)}</dd>
                  </div>
                ))}
              </dl>

              {wrongUserType && (
                <>
                  <p className="mt-3 text-[13px] font-semibold text-amber-900">
                    {es
                      ? 'Esta cuenta es de cliente, no de contratista, así que no puede recibir créditos de membresía. Conviértala a contratista y luego reenvíe el pago. La contraseña del cliente no cambia.'
                      : 'This is a homeowner account, not a contractor account, so it cannot hold membership credits. Convert it to a contractor account, then replay the payment. The customer keeps their existing password.'}
                  </p>
                  <button
                    onClick={() => createAccount(true)}
                    disabled={creating}
                    className="mt-3 inline-flex min-h-[48px] items-center gap-2 rounded-xl bg-amber-600 px-5 py-3 text-sm font-bold text-white hover:bg-amber-700 disabled:opacity-50"
                    style={{ touchAction: 'manipulation' }}
                  >
                    {creating ? (
                      <RefreshCw className="h-4 w-4 animate-spin" />
                    ) : (
                      <ArrowRightLeft className="h-4 w-4" />
                    )}
                    {es ? 'Convertir a contratista' : 'Convert to contractor'}
                  </button>
                </>
              )}
            </div>
          </div>
        )}

        {/* --- outcome D: the function confirmed there is no profile --------- */}
        {lookupState === 'none' && (
          <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50/70 p-4">
            <p className="flex items-center gap-2 text-sm font-extrabold text-amber-900">
              <AlertTriangle className="h-4 w-4" />
              {es ? 'Ninguna cuenta de GIGGONE usa ese correo' : 'No GIGGONE account uses that email'}
            </p>
            <p className="mt-1 text-[13px] text-amber-900">
              {es
                ? 'Comprobado: ningún perfil usa este correo. Los créditos solo pueden asignarse a una cuenta existente, así que cree la cuenta del contratista y luego reenvíe el pago.'
                : 'Checked: no profile uses this email. Credits can only be assigned to an existing account, so create the contractor account, then replay the payment.'}
            </p>
            {lookup?.events?.length > 0 && (
              <p className="mt-1 text-[13px] font-semibold text-amber-900">
                {es
                  ? `Hay ${lookup.events.length} evento(s) de pago recibidos para este correo sin cuenta asociada.`
                  : `${lookup.events.length} payment event(s) were received for this email with no account attached.`}
              </p>
            )}
            <button
              onClick={() => createAccount(false)}
              disabled={creating || !email.trim()}
              className="mt-3 inline-flex min-h-[48px] items-center gap-2 rounded-xl bg-amber-600 px-5 py-3 text-sm font-bold text-white hover:bg-amber-700 disabled:opacity-50"
              style={{ touchAction: 'manipulation' }}
            >
              {creating ? <RefreshCw className="h-4 w-4 animate-spin" /> : <UserPlus className="h-4 w-4" />}
              {es ? 'Crear la cuenta del contratista' : 'Create the contractor account'}
            </button>
          </div>
        )}


        {tempPassword && (
          <div className="mt-3 rounded-xl border border-slate-300 bg-slate-50 p-4">
            <p className="text-[11px] font-bold uppercase tracking-wide text-slate-500">
              {es ? 'Contraseña temporal (se muestra una sola vez)' : 'Temporary password (shown once)'}
            </p>
            <div className="mt-1 flex flex-wrap items-center gap-2">
              <code className="font-mono text-sm font-bold text-slate-900">{tempPassword}</code>
              <button
                onClick={copyPassword}
                className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-2.5 py-1 text-xs font-bold text-slate-700"
              >
                {copied ? <Check className="h-3.5 w-3.5 text-emerald-600" /> : <Copy className="h-3.5 w-3.5" />}
                {copied ? 'Copied' : 'Copy'}
              </button>
            </div>
            <p className="mt-1.5 text-xs text-slate-500">
              {es
                ? 'Envíela al cliente o pídale que use “Olvidé mi contraseña”. Debe cambiarla al entrar.'
                : 'Send it to the customer or ask them to use “Forgot password”. They should change it on first login.'}
            </p>
          </div>
        )}
      </div>

      {/* ---------- step 2: replay ---------- */}
      <div className="mt-4 rounded-2xl border border-slate-200 bg-white p-5">
        <p className="text-sm font-extrabold text-slate-900">
          {es ? 'Paso 2 · Reenvíe la transacción del procesador' : 'Step 2 · Replay the processor transaction'}
        </p>
        <div className="mt-3 grid gap-3 sm:grid-cols-2">
          <div>
            <label className={labelCls}>
              {es ? 'ID de transacción (obligatorio)' : 'Transaction id (required)'}
            </label>
            <input
              value={transactionId}
              onChange={(e) => setTransactionId(e.target.value)}
              placeholder="ch_3Q… / pi_3Q… / GHL transaction id"
              className={`${field} mt-1`}
            />
            <p className="mt-1 text-[11px] text-slate-500">
              {es
                ? 'Payments > Transacciones en el CRM, o el ID de cargo de Stripe. Es lo que impide un crédito duplicado.'
                : 'CRM Payments > Transactions, or the Stripe charge id. This is what prevents a duplicate credit.'}
            </p>
          </div>
          <div>
            <label className={labelCls}>{es ? 'Plan pagado' : 'Plan paid for'}</label>
            <select value={plan} onChange={(e) => setPlan(e.target.value)} className={`${field} mt-1`}>
              {Object.keys(WEBHOOK_PLAN_CENTS).map((code) => (
                <option key={code} value={code}>
                  {code} — {money(WEBHOOK_PLAN_CENTS[code])} — {WEBHOOK_PLAN_CREDITS[code]}{' '}
                  {es ? 'créditos' : 'credits'}
                </option>
              ))}
            </select>
            <p className="mt-1 text-[11px] text-slate-500">
              {es
                ? 'El importe se fija al precio mensual del plan; un importe distinto se rechaza.'
                : 'The amount is locked to the plan\u2019s monthly price; a different amount is rejected.'}
            </p>
          </div>
          <div>
            <label className={labelCls}>{es ? 'ID de contacto del CRM (opcional)' : 'CRM contact id (optional)'}</label>
            <input value={contactId} onChange={(e) => setContactId(e.target.value)} className={`${field} mt-1`} />
          </div>
          <div>
            <label className={labelCls}>
              {es ? 'ID de suscripción de Stripe (opcional)' : 'Stripe subscription id (optional)'}
            </label>
            <input
              value={subscriptionId}
              onChange={(e) => setSubscriptionId(e.target.value)}
              className={`${field} mt-1`}
            />
          </div>
          <div className="sm:col-span-2">
            <label className={labelCls}>{es ? 'Motivo (queda registrado)' : 'Reason (recorded)'}</label>
            <input value={reason} onChange={(e) => setReason(e.target.value)} className={`${field} mt-1`} />
          </div>
        </div>

        <button
          onClick={runRecovery}
          disabled={!canRecover}
          className="mt-4 inline-flex min-h-[52px] items-center gap-2 rounded-xl bg-orange-500 px-6 py-3 text-base font-extrabold text-white hover:bg-orange-600 disabled:opacity-50"
          style={{ touchAction: 'manipulation' }}
        >
          {running ? <RefreshCw className="h-5 w-5 animate-spin" /> : <RotateCcw className="h-5 w-5" />}
          {running
            ? es
              ? 'Reenviando…'
              : 'Replaying…'
            : es
            ? 'Reenviar el pago y asignar créditos'
            : 'Replay the payment and assign credits'}
        </button>
        {(lookupState === 'idle' || lookupState === 'none') && (
          <p className="mt-2 text-xs font-bold text-slate-500">
            {es ? 'Primero busque o cree la cuenta.' : 'Look up or create the account first.'}
          </p>
        )}
        {(lookupState === 'auth' || lookupState === 'error') && (
          <p className="mt-2 text-xs font-bold text-red-700">
            {es
              ? 'No se pudo comprobar la cuenta. Resuelva el error de arriba y vuelva a buscar.'
              : 'The account could not be checked. Clear the error above, then look up again.'}
          </p>
        )}

        {wrongUserType && (
          <p className="mt-2 text-xs font-bold text-amber-700">
            {es
              ? 'Convierta la cuenta a contratista antes de reenviar el pago.'
              : 'Convert the account to a contractor account before replaying the payment.'}
          </p>
        )}
      </div>

      {/* ---------- manual grant reconciliation notice ---------- */}
      <div className="mt-4 rounded-2xl border border-sky-200 bg-sky-50/70 p-5">
        <p className="flex items-center gap-2 text-sm font-extrabold text-sky-900">
          <ArrowRightLeft className="h-4 w-4" />
          {es ? 'Si la membresía se otorgó a mano' : 'If the membership was granted by hand'}
        </p>
        <p className="mt-1 text-[13px] leading-relaxed text-sky-900">
          {es
            ? 'Cuando un administrador activa la membresía sin un ID de transacción, la suscripción queda marcada como admin-recovery y el pago queda con reconciliation_required. Si el CRM entrega después la transacción real del mismo plan, el webhook la reconcilia: adjunta el ID real, NO vuelve a otorgar créditos y NO cuenta el dinero dos veces (resultado reconciled:manual_grant).'
            : 'When an admin activates a membership without a transaction id, the subscription is tagged admin-recovery and the payment row carries reconciliation_required. If the CRM later delivers the real transaction for the same plan, the webhook reconciles it: the real id is attached, credits are NOT re-issued and the money is NOT counted twice (result reconciled:manual_grant).'}
        </p>
      </div>


      {/* ---------- evidence ---------- */}
      {result && (
        <div
          className={`mt-4 rounded-2xl border p-5 ${
            result.evidence?.creditsCorrect ? 'border-emerald-200 bg-emerald-50/70' : 'border-red-200 bg-red-50/70'
          }`}
        >
          <p className="flex items-center gap-2 text-base font-extrabold text-slate-900">
            {result.evidence?.creditsCorrect ? (
              <CheckCircle2 className="h-5 w-5 text-emerald-600" />
            ) : (
              <XCircle className="h-5 w-5 text-red-600" />
            )}
            {result.evidence?.creditsCorrect
              ? es
                ? 'Recuperación completada'
                : 'Recovery complete'
              : es
              ? 'La recuperación no terminó'
              : 'Recovery did not complete'}
          </p>

          {result.error && <p className="mt-2 text-sm font-bold text-red-700">{String(result.error)}</p>}
          {result.message && <p className="mt-2 text-sm text-slate-700">{result.message}</p>}

          {ev && (
            <dl className="mt-3 grid gap-x-6 gap-y-1.5 text-[13px] sm:grid-cols-2">
              {[
                [es ? 'Cuenta' : 'Account', `${ev.account?.email} (${ev.account?.id})`],
                [es ? 'Transacción' : 'Transaction', ev.transactionId],
                [
                  es ? 'Evento de pago' : 'Payment event',
                  ev.paymentEvent
                    ? `${ev.paymentEvent.type} · ${ev.paymentEvent.result} · ${
                        ev.paymentEvent.matchedAccount ? (es ? 'cuenta vinculada' : 'account matched') : '—'
                      }`
                    : '—',
                ],
                [
                  es ? 'Registro de pago' : 'Payment record',
                  ev.payment ? `${money(ev.payment.amount)} · ${ev.payment.status}` : '—',
                ],
                [
                  es ? 'Suscripción' : 'Subscription',
                  ev.subscription
                    ? `${ev.subscription.plan} · ${ev.subscription.status} · ${ev.subscription.includedLeads} ${
                        es ? 'créditos' : 'credits'
                      } · ${es ? 'pago' : 'payment'} ${ev.subscription.paymentsMade}/${ev.subscription.termMonths}`
                    : '—',
                ],
                [
                  es ? 'Créditos' : 'Credits',
                  `${ev.creditsBefore} → ${ev.creditsAfter} (${es ? 'esperado' : 'expected'} ${ev.expectedCredits})`,
                ],
                [
                  es ? 'Libro de créditos' : 'Credit ledger',
                  result.ledgerWritten ? (es ? 'escrito' : 'written') : es ? 'ya existía' : 'already present',
                ],
                [
                  es ? 'Correo al cliente' : 'Customer email',
                  result.emailSent ? (es ? 'enviado' : 'sent') : result.emailError || (es ? 'no enviado' : 'not sent'),
                ],
                [es ? 'Entrega duplicada' : 'Duplicate delivery', result.duplicate ? 'yes' : 'no'],
                [es ? '¿Se cobró otra vez?' : 'Charged again?', 'no'],
              ].map(([k, v]) => (
                <div key={String(k)} className="flex justify-between gap-3 border-b border-white/70 py-1">
                  <dt className="font-bold text-slate-600">{k}</dt>
                  <dd className="text-right font-semibold text-slate-900">{String(v)}</dd>
                </div>
              ))}
            </dl>
          )}
        </div>
      )}

      {/* ---------- unmatched deliveries ---------- */}
      <h3 className="mt-10 flex items-center gap-2 text-sm font-extrabold uppercase tracking-wide text-slate-900">
        <AlertTriangle className="h-4 w-4 text-amber-500" />
        {es ? 'Pagos recibidos sin cuenta asociada' : 'Payments received with no matching account'}
      </h3>
      <p className="mt-1 max-w-3xl text-sm text-slate-500">
        {es
          ? 'El webhook llegó y fue verificado, pero ningún perfil usa ese correo, así que no se otorgó ningún crédito. Cree la cuenta y reenvíe la transacción arriba.'
          : 'The webhook arrived and was verified, but no profile uses that email, so no credit was granted. Create the account and replay the transaction above.'}
      </p>
      <div className="mt-3 overflow-x-auto rounded-2xl border border-slate-200 bg-white">
        {pending.length === 0 ? (
          <p className="p-6 text-center text-sm text-slate-500">
            {es
              ? 'Ninguno. Todos los eventos recibidos encontraron su cuenta.'
              : 'None. Every received event matched an account.'}
          </p>
        ) : (
          <table className="w-full min-w-[720px] text-left text-sm">
            <thead className="bg-slate-50 text-xs font-bold uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-3">{es ? 'Correo' : 'Email'}</th>
                <th className="px-4 py-3">{es ? 'Plan' : 'Plan'}</th>
                <th className="px-4 py-3">{es ? 'Importe' : 'Amount'}</th>
                <th className="px-4 py-3">{es ? 'Transacción' : 'Transaction'}</th>
                <th className="px-4 py-3">{es ? 'Cuándo' : 'When'}</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {pending.map((p) => (
                <tr key={p.id}>
                  <td className="px-4 py-3 font-semibold text-slate-800">{p.email || '—'}</td>
                  <td className="px-4 py-3 capitalize text-slate-600">{p.plan_code || '—'}</td>
                  <td className="px-4 py-3 text-slate-600">{money(p.amount || 0)}</td>
                  <td className="px-4 py-3 font-mono text-xs text-slate-500">{p.event_id.slice(0, 22)}</td>
                  <td className="px-4 py-3 text-xs text-slate-400">{new Date(p.created_at).toLocaleString()}</td>
                  <td className="px-4 py-3 text-right">
                    <button
                      onClick={() => {
                        setEmail(p.email || '');
                        setTransactionId(p.event_id);
                        if (p.plan_code && WEBHOOK_PLAN_CENTS[p.plan_code]) setPlan(p.plan_code);
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                      }}
                      className="rounded-lg bg-slate-900 px-3 py-1.5 text-xs font-bold text-white hover:bg-slate-800"
                    >
                      {es ? 'Cargar' : 'Load'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default AdminPaymentRecovery;
